import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import ClientSwitcher from "@/components/ClientSwitcher";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AppointmentForm() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [types, setTypes] = useState(["Primary Care", "Dental", "Specialist"]);
  const [form, setForm] = useState({
    date: todayISO(),
    appt_type: "",
    doctor_clinic: "",
    important_details: "",
    follow_up: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
      const saved = localStorage.getItem("oppo_active_client");
      setClientId(data.find((c) => c.id === saved)?.id || data[0]?.id || "");
    });
    api.get("/form-configs/appointment").then(({ data }) => {
      if (data?.fields?.appt_types) setTypes(data.fields.appt_types);
    });
  }, []);

  const submit = async () => {
    if (!clientId) return toast.error("Select a client");
    if (!form.appt_type) return toast.error("Select an appointment type");
    if (!form.important_details.trim()) return toast.error("Important details required");
    setSubmitting(true);
    try {
      await api.post("/appointments", { client_id: clientId, ...form });
      toast.success("Appointment logged");
      navigate("/provider");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="appointment-form">
      <div>
        <p className="text-label">Form</p>
        <h2 className="font-display text-2xl font-bold tracking-tight">Medical / Dental Appointment</h2>
      </div>

      <ClientSwitcher clients={clients} activeId={clientId} onChange={setClientId} />

      <Card className="p-4 space-y-4">
        <div>
          <Label htmlFor="appt-date" className="text-label">Date</Label>
          <Input
            id="appt-date" type="date" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="h-12 mt-1" data-testid="appt-date"
          />
        </div>
        <div>
          <Label className="text-label">Type</Label>
          <Select value={form.appt_type} onValueChange={(v) => setForm({ ...form, appt_type: v })}>
            <SelectTrigger className="h-12 mt-1" data-testid="appt-type">
              <SelectValue placeholder="Select type..." />
            </SelectTrigger>
            <SelectContent>
              {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="clinic" className="text-label">Doctor / Clinic Name (optional)</Label>
          <Input
            id="clinic" value={form.doctor_clinic}
            onChange={(e) => setForm({ ...form, doctor_clinic: e.target.value })}
            className="h-12 mt-1" data-testid="appt-doctor"
          />
        </div>
        <div>
          <Label htmlFor="details" className="text-label">Important Details *</Label>
          <Textarea
            id="details" rows={4} value={form.important_details}
            onChange={(e) => setForm({ ...form, important_details: e.target.value })}
            className="mt-1" data-testid="appt-details"
            placeholder="Reason, recommendations, prescriptions..."
          />
        </div>
        <div>
          <Label htmlFor="follow" className="text-label">Follow-up Actions (optional)</Label>
          <Textarea
            id="follow" rows={3} value={form.follow_up}
            onChange={(e) => setForm({ ...form, follow_up: e.target.value })}
            className="mt-1" data-testid="appt-followup"
          />
        </div>
      </Card>

      <Button
        onClick={submit}
        className="w-full h-14 text-base font-semibold shadow-lg"
        disabled={submitting}
        data-testid="submit-appointment"
      >
        {submitting ? "Submitting..." : "Add Medical/Dental Appointment"}
      </Button>
    </div>
  );
}
